// Brand profile — GET/PUT /restaurants/{rid}. The PUT is whole-object:
// name, logoUrl (nullable), timezone and country all travel every save.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { api } from '../../lib/api';
import { AU_TIMEZONES, type Restaurant, type RestaurantInput } from './types';

export function BrandProfile() {
  const { rid = '' } = useParams();
  const [form, setForm] = useState<RestaurantInput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const take = (r: Restaurant) => setForm({ name: r.name, logoUrl: r.logoUrl, timezone: r.timezone, country: r.country });

  useEffect(() => {
    let live = true;
    api.get<Restaurant>(`/restaurants/${rid}`)
      .then((r) => { if (live) take(r); })
      .catch((e: unknown) => { if (live) setError(e instanceof Error ? e.message : 'Failed to load.'); });
    return () => { live = false; };
  }, [rid]);

  async function save() {
    if (!form) return;
    setSaving(true);
    setSaved(false);
    try {
      take(await api.put<Restaurant>(`/restaurants/${rid}`, { ...form, name: form.name.trim(), logoUrl: form.logoUrl?.trim() || null }));
      setError(null);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  const edit = (patch: Partial<RestaurantInput>) => { if (form) { setForm({ ...form, ...patch }); setSaved(false); } };
  const zones = form && !AU_TIMEZONES.includes(form.timezone) ? [form.timezone, ...AU_TIMEZONES] : AU_TIMEZONES;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Restaurant · Settings</div>
          <h1>Brand profile</h1>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}
      {form === null && !error && <div className="loading">Loading…</div>}
      {form && (
        <div className="panel" style={{ maxWidth: 560 }} data-section="profile">
          <h2>Profile</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
            <div className="field">
              <label htmlFor="brand-name">Name</label>
              <input id="brand-name" className="input" value={form.name} onChange={(e) => edit({ name: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="brand-logo">Logo URL (optional)</label>
              <input id="brand-logo" className="input" value={form.logoUrl ?? ''} placeholder="https://…"
                onChange={(e) => edit({ logoUrl: e.target.value || null })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label htmlFor="brand-tz">Timezone</label>
                <select id="brand-tz" className="input" value={form.timezone} onChange={(e) => edit({ timezone: e.target.value })}>
                  {zones.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="brand-country">Country</label>
                <input id="brand-country" className="input" value={form.country} placeholder="AU" maxLength={2}
                  onChange={(e) => edit({ country: e.target.value.toUpperCase() })} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="btn btn-primary btn-sm"
                disabled={saving || !form.name.trim() || !form.timezone || !form.country}
                onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              {saved && <span style={{ color: 'var(--ok)', fontSize: 13 }} role="status">Saved.</span>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
